import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Presentation, Download, ChevronLeft, ChevronRight, X, ArrowLeft, Users, MapPin, Cloud, AlertTriangle, Target, Shield, Package, Building2, HelpCircle, Loader2, Activity, Monitor, Play, Pause, Maximize2 } from 'lucide-react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import secureStorage from '../utils/secureStorage';
import { MapContainer, TileLayer, Marker, Polygon, Circle, Polyline, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const API = `${API_BASE_URL}/api`;

// Fix default Leaflet marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Interactive map component for Slide 3
const BriefingMap = ({ centerLat, centerLng, zoomLevel, mapObjects, fallbackImage, totalFeatures }) => {
  const mapRef = useRef(null);

  const parseCoords = (geom) => {
    const coords = geom?.coordinates;
    if (!coords) return null;
    return coords;
  };

  return (
    <div className="space-y-2" data-testid="briefing-map-interactive">
      <div className="rounded-lg border border-gray-700 overflow-hidden" style={{ height: '60vh' }}>
        <MapContainer
          center={[centerLat || 49, centerLng || -97]}
          zoom={zoomLevel || 8}
          style={{ height: '100%', width: '100%' }}
          ref={mapRef}
          scrollWheelZoom={true}
          zoomControl={true}
        >
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            attribution="Esri"
          />
          {mapObjects.map((obj, i) => {
            const coords = parseCoords(obj.geometry);
            if (!coords) return null;
            const color = obj.color || '#3388ff';

            if (obj.type === 'marker') {
              return <CircleMarker key={`m-${i}`} center={[coords[1], coords[0]]} radius={8} pathOptions={{ color: color, fillColor: color, fillOpacity: 0.8, weight: 2 }} />;
            }
            if (obj.type === 'circle') {
              return <Circle key={`c-${i}`} center={[coords[1], coords[0]]} radius={obj.radius || 1000} pathOptions={{ color: color, fillColor: color, fillOpacity: 0.3, weight: 2 }} />;
            }
            if (obj.type === 'polygon' || obj.type === 'rectangle') {
              const ring = coords[0] && Array.isArray(coords[0][0]) ? coords[0] : coords;
              const positions = ring.map(c => [c[1], c[0]]);
              return <Polygon key={`p-${i}`} positions={positions} pathOptions={{ color: color, fillColor: color, fillOpacity: 0.3, weight: 2 }} />;
            }
            if (obj.type === 'line') {
              const positions = coords.map(c => [c[1], c[0]]);
              return <Polyline key={`l-${i}`} positions={positions} pathOptions={{ color: color, weight: 3 }} />;
            }
            return null;
          })}
        </MapContainer>
      </div>
      <p className="text-gray-500 text-xs text-center">{totalFeatures} map feature{totalFeatures !== 1 ? 's' : ''} — scroll to zoom, drag to pan</p>
    </div>
  );
};

const SLIDE_ICONS = [
  Presentation, Users, MapPin, Cloud, AlertTriangle,
  Activity, Target, Shield, Shield, Shield, Package, Building2, HelpCircle
];

const BriefingPreview = ({ exerciseId, exerciseName, theme, onClose, displayMode = false }) => {
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);
  const [autoPlay, setAutoPlay] = useState(false);
  const [autoPlaySeconds, setAutoPlaySeconds] = useState(12);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!exerciseId) {
      setError('No exercise selected');
      setLoading(false);
      return;
    }
    const fetchPreview = async () => {
      try {
        setLoading(true);
        const token = secureStorage.getToken();
        const response = await axios.get(`${API}/exercises/${exerciseId}/briefing/preview`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 30000
        });
        setSlides(response.data.slides || []);
      } catch (err) {
        const detail = err.response?.data?.detail || err.message || 'Unknown error';
        setError(`Failed to load briefing preview: ${detail}`);
        console.error('Briefing preview error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchPreview();
  }, [exerciseId]);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      const token = secureStorage.getToken();
      const response = await axios.get(`${API}/exercises/${exerciseId}/briefing/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const safeName = (exerciseName || 'briefing').replace(/\s+/g, '_');
      link.setAttribute('download', `IC_Briefing_${safeName}.pptx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setDownloading(false);
    }
  };

  const slide = slides[currentSlide];
  const SlideIcon = SLIDE_ICONS[currentSlide] || Presentation;

  // Keyboard navigation (both modes) + auto-advance (display mode only)
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === 'PageDown' || e.key === ' ') {
        e.preventDefault();
        setCurrentSlide((c) => Math.min(slides.length - 1, c + 1));
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        setCurrentSlide((c) => Math.max(0, c - 1));
      } else if (e.key === 'Home') {
        setCurrentSlide(0);
      } else if (e.key === 'End') {
        setCurrentSlide(slides.length - 1);
      } else if ((e.key === 'f' || e.key === 'F') && displayMode) {
        toggleFullscreen();
      } else if (e.key === 'Escape' && displayMode) {
        if (document.fullscreenElement) document.exitFullscreen();
        else window.close();
      } else if ((e.key === 'p' || e.key === 'P') && displayMode) {
        setAutoPlay((v) => !v);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [slides.length, displayMode]);

  useEffect(() => {
    if (!displayMode || !autoPlay || slides.length === 0) return;
    const t = setInterval(() => {
      setCurrentSlide((c) => (c + 1) % slides.length);
    }, autoPlaySeconds * 1000);
    return () => clearInterval(t);
  }, [autoPlay, autoPlaySeconds, slides.length, displayMode]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      (containerRef.current || document.documentElement).requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const openDisplayMode = () => {
    const url = exerciseId ? `#display-briefing?exercise=${exerciseId}` : '#display-briefing';
    window.open(url, '_blank');
  };

  const renderSlideContent = () => {
    if (!slide) return null;
    const content = slide.content || {};

    switch (slide.number) {
      case 1: // Exercise Overview
        return (
          <div className="space-y-6">
            <div className="text-center mb-4">
              <h2 className="text-3xl font-bold text-white mt-1">{content.exercise_name}</h2>
              <p className="text-orange-400 text-lg mt-2">Incident Commander Briefing</p>
            </div>
            {content.exercise_image && (
              <div className="flex justify-center" data-testid="briefing-exercise-image">
                <img
                  src={content.exercise_image}
                  alt={content.exercise_name || 'Exercise'}
                  className="max-w-full max-h-[42vh] rounded-lg border border-gray-700 shadow-lg object-cover"
                />
              </div>
            )}
            <div className="grid grid-cols-3 gap-4">
              <InfoCard label="Exercise Type" value={content.exercise_type} />
              <InfoCard label="Location" value={content.location} />
              <InfoCard label="Dates" value={content.dates} />
            </div>
            <InfoCard label="Incident Commander" value={content.incident_commander} full />
            <p className="text-center text-orange-500 text-sm font-semibold tracking-widest mt-4">powered by EXRSIM</p>
          </div>
        );

      case 2: // ICS Organization
        return (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-x-6 gap-y-1">
              <div className="text-orange-400 text-sm font-semibold pb-1 border-b border-orange-500/30">Position</div>
              <div className="text-orange-400 text-sm font-semibold pb-1 border-b border-orange-500/30">Assigned</div>
              {(content.positions || []).map((pos, i) => (
                <React.Fragment key={`pos-${i}`}>
                  <div className={`py-2 text-sm font-medium ${i === 0 ? 'text-orange-400' : 'text-white'}`}>
                    {pos.title}
                  </div>
                  <div className={`py-2 text-sm flex items-center gap-2 ${pos.name !== 'Vacant' ? 'text-green-400' : 'text-red-400'}`}>
                    {pos.avatar && (
                      <img
                        src={pos.avatar.startsWith('data:') ? pos.avatar : `data:image/png;base64,${pos.avatar}`}
                        alt=""
                        className="w-7 h-7 rounded-full object-cover border border-gray-600"
                        data-testid={`briefing-avatar-${i}`}
                      />
                    )}
                    {pos.name}
                  </div>
                </React.Fragment>
              ))}
            </div>
          </div>
        );

      case 3: // Map Overview - interactive Leaflet map
        return (
          <BriefingMap
            centerLat={content.center_lat}
            centerLng={content.center_lng}
            zoomLevel={content.zoom_level}
            mapObjects={content.map_objects || []}
            fallbackImage={content.map_image_b64}
            totalFeatures={content.total_features}
          />
        );

      case 4: // Weather
        return (
          <div className="space-y-4" data-testid="briefing-weather-slide">
            {content.weather_location && (
              <p className="text-gray-400 text-sm">Satellite Image: {content.weather_location}</p>
            )}
            {content.weather_image_url ? (
              <div className="flex justify-center">
                <img
                  src={content.weather_image_url}
                  alt="Weather Satellite"
                  className="max-w-full max-h-[60vh] rounded-lg border border-gray-700"
                  data-testid="briefing-weather-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling && (e.target.nextSibling.style.display = 'flex');
                  }}
                />
                <div className="hidden items-center justify-center py-8 text-gray-500">
                  <Cloud className="h-12 w-12 mr-3 opacity-50" />
                  <span>Weather image could not be loaded</span>
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <Cloud className="h-16 w-16 text-gray-600 mb-4" />
                <p className="text-gray-500 text-center">No weather data available</p>
                <p className="text-gray-600 text-sm mt-1">Set a weather location preference to see satellite imagery</p>
              </div>
            )}
          </div>
        );

      case 5: // Situation
        return (
          <div className="space-y-4">
            {content.scenario_name && (
              <h3 className="text-orange-400 text-lg font-semibold">{content.scenario_name}</h3>
            )}
            {content.scenario_description && (
              <p className="text-gray-300 text-sm leading-relaxed">{content.scenario_description}</p>
            )}
            {(content.entries || []).length > 0 && (
              <div className="space-y-2 mt-3">
                <h4 className="text-orange-400 text-sm font-semibold">Scenario Entries</h4>
                {content.entries.map((e, i) => (
                  <div key={`entry-${i}`} className="bg-gray-800/60 rounded-lg p-3">
                    <div className="text-white text-sm font-medium">{e.title}</div>
                    {e.description && <div className="text-gray-400 text-xs mt-1">{e.description}</div>}
                  </div>
                ))}
              </div>
            )}
            {!content.scenario_description && (content.entries || []).length === 0 && (
              <p className="text-gray-500 text-center py-4">No scenario information available</p>
            )}
          </div>
        );

      case 6: // Exercise Status
        return (
          <div className="grid grid-cols-3 gap-4 mt-4">
            <InfoCard label="Status" value={content.status} />
            <InfoCard label="Emergency Phase" value={content.phase} />
            <InfoCard label="Operational Period" value={`${content.start_date} ${content.start_time || ''}\nto\n${content.end_date} ${content.end_time || ''}`} />
          </div>
        );

      case 7: // Objectives
        return (
          <div className="space-y-3">
            {(content.objectives || []).length === 0 ? (
              <p className="text-gray-500 text-center py-4">No objectives defined</p>
            ) : (
              content.objectives.map((obj, i) => (
                <div key={`obj-${i}`} className="bg-gray-800/60 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-orange-400 text-sm font-semibold">{obj.title}</span>
                    <span className={`text-xs font-semibold px-2 py-1 rounded ${
                      obj.status === 'completed' ? 'bg-green-900/50 text-green-400' :
                      obj.status === 'in-progress' ? 'bg-yellow-900/50 text-yellow-400' :
                      'bg-gray-700 text-gray-400'
                    }`}>{(obj.status || 'pending').replace('-', ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>
                  </div>
                  {obj.description && (
                    <p className="text-gray-400 text-xs mb-2">{obj.description}</p>
                  )}
                  {obj.strategies && obj.strategies.length > 0 ? (
                    <div className="ml-3 space-y-1.5 border-l-2 border-orange-500/30 pl-3">
                      <span className="text-gray-500 text-xs font-medium">Strategies:</span>
                      {obj.strategies.map((s, j) => (
                        <div key={`strat-${j}`} className="text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-orange-500 text-xs">&#9679;</span>
                            <span className="text-white">{typeof s === 'string' ? s : s.title}</span>
                            {s.status && (
                              <span className={`text-[10px] px-1.5 py-0.5 rounded ${
                                s.status === 'completed' ? 'bg-green-900/40 text-green-400' :
                                s.status === 'in-progress' ? 'bg-yellow-900/40 text-yellow-400' :
                                'bg-gray-700/50 text-gray-500'
                              }`}>{s.status.replace('-', ' ')}</span>
                            )}
                          </div>
                          {s.description && (
                            <p className="text-gray-500 text-xs ml-4 mt-0.5">{s.description}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-600 text-xs ml-3 italic">No strategies defined</p>
                  )}
                </div>
              ))
            )}
          </div>
        );

      case 8: // Safety Officer - Incident Details
      case 9: // Safety Officer - Personnel and Resources
      case 10: // Safety Officer - Safety-Specific Data
        const categoryLabels = {
          'incident-details': 'Incident Details',
          'personnel-resources': 'Personnel and Resources',
          'safety-specific': 'Safety-Specific Data',
          'incident-findings': 'Incident Findings & Recommendations',
        };
        const categoryFields = {
          'incident-details': [
            ['incident_location_time', 'Incident Location'],
            ['nature_of_threat', 'Nature of Threat'],
            ['incident_objectives', 'Safety Objectives'],
            ['current_planned_actions', 'Planned Actions'],
          ],
          'personnel-resources': [
            ['personnel_involved', 'Personnel Involved'],
            ['equipment_status', 'Equipment Status'],
            ['resources_ordered', 'Resources Ordered'],
          ],
          'safety-specific': [
            ['hazard_assessment', 'Hazard Assessment'],
            ['ppe_scba_checks', 'PPE/SCBA Checks'],
            ['ignition_source_checks', 'Ignition Source Checks'],
            ['medical_evacuation_plan', 'Medical Evac Plan'],
            ['site_conditions', 'Site Conditions'],
          ],
          'incident-findings': [
            ['injuries_incidents', 'Injuries/Incidents'],
            ['physical_evidence', 'Physical Evidence'],
            ['recommendations', 'Recommendations'],
          ],
        };
        const safetyCategory = content.category;
        const safetySubtitle = content.subtitle || categoryLabels[safetyCategory] || 'Safety Officer';
        return (
          <div className="space-y-3" data-testid={`briefing-safety-${safetyCategory}`}>
            <div className="flex items-center gap-2 pb-2 border-b border-orange-500/30 mb-2">
              <Shield className="h-5 w-5 text-orange-400" />
              <h3 className="text-orange-400 text-lg font-semibold">{safetySubtitle}</h3>
              <span className="text-gray-500 text-xs ml-auto">{(content.entries || []).length} {(content.entries || []).length === 1 ? 'entry' : 'entries'}</span>
            </div>
            {(content.entries || []).length === 0 ? (
              <p className="text-gray-500 text-center py-4">No {safetySubtitle.toLowerCase()} entries reported</p>
            ) : (
              content.entries.map((entry, i) => {
                const fields = categoryFields[safetyCategory] || [];
                const filledFields = fields.filter(([key]) => entry[key]);
                return (
                  <div key={`safety-${slide.number}-${i}`} className="bg-gray-800/60 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="text-orange-400 text-sm font-semibold">{entry.title}</span>
                      </div>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                        entry.priority === 'critical' ? 'bg-red-900/50 text-red-400' :
                        entry.priority === 'high' ? 'bg-orange-900/50 text-orange-400' :
                        entry.priority === 'medium' ? 'bg-yellow-900/50 text-yellow-400' :
                        'bg-gray-700 text-gray-400'
                      }`}>{entry.priority}</span>
                    </div>
                    {filledFields.length > 0 && (
                      <div className="space-y-1 ml-2">
                        {filledFields.map(([key, label]) => (
                          <div key={key} className="flex gap-2 text-xs">
                            <span className="text-gray-400 font-medium w-36 flex-shrink-0">{label}:</span>
                            <span className="text-white">{entry[key]}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        );

      case 11: // Resources
        return (
          <div className="mt-2">
            <table className="w-full text-sm" data-testid="resource-summary-table">
              <thead>
                <tr className="border-b border-orange-500/40">
                  <th className="text-left py-2 px-3 text-orange-400 font-semibold">Category</th>
                  <th className="text-center py-2 px-3 text-orange-400 font-semibold w-24">Request</th>
                  <th className="text-center py-2 px-3 text-orange-400 font-semibold w-24">Have</th>
                  <th className="text-center py-2 px-3 text-orange-400 font-semibold w-24">Need</th>
                </tr>
              </thead>
              <tbody>
                {(content.categories || []).map((cat, i) => (
                  <tr key={`res-${i}`} className={i % 2 === 0 ? 'bg-gray-800/40' : ''}>
                    <td className="py-1.5 px-3 text-white font-medium">{cat.label}</td>
                    <td className="py-1.5 px-3 text-center text-gray-300">
                      {cat.label === 'Personnel' ? '-' : cat.request}
                    </td>
                    <td className="py-1.5 px-3 text-center text-orange-400 font-bold">{cat.have}</td>
                    <td className="py-1.5 px-3 text-center text-gray-300">
                      {cat.label === 'Personnel' ? '-' : cat.need}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 12: // Organizations
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="briefing-orgs-grid">
            {(content.agencies || []).length === 0 ? (
              <p className="text-gray-500 text-center py-4 md:col-span-2">No organizations registered</p>
            ) : (
              content.agencies.map((a, i) => {
                const name = typeof a === 'string' ? a : a.name;
                const image = typeof a === 'object' ? a.image : null;
                const description = typeof a === 'object' ? (a.description || '') : '';
                const personCount = typeof a === 'object' ? (a.person_count || 0) : 0;
                return (
                  <div key={`org-${i}`} className="bg-gray-800/60 border border-gray-700 rounded-lg p-4 flex items-start gap-3" data-testid={`briefing-org-${i}`}>
                    {image ? (
                      <img
                        src={image}
                        alt=""
                        className="w-[56px] h-[56px] rounded object-cover border border-gray-600 flex-shrink-0"
                        data-testid={`briefing-agency-image-${i}`}
                      />
                    ) : (
                      <div className="w-[56px] h-[56px] rounded bg-gray-700 border border-gray-600 flex-shrink-0 flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-gray-500" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h4 className="text-white font-semibold text-base truncate">{name}</h4>
                        <span className="text-xs px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-300 font-medium">
                          {personCount} {personCount === 1 ? 'person' : 'persons'}
                        </span>
                      </div>
                      {description && (
                        <p className="text-gray-300 text-sm mt-1 leading-relaxed">{description}</p>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        );

      case 13: // Questions
        return (
          <div className="flex flex-col items-center justify-center py-8">
            <h2 className="text-4xl font-bold text-orange-500 mb-4">Questions?</h2>
            <p className="text-white text-lg">{content.exercise_name}</p>
            <p className="text-gray-500 mt-2 text-sm">EXRSIM - Plan. Execute. Analyse.</p>
          </div>
        );

      default:
        return <p className="text-gray-500">No content</p>;
    }
  };

  return (
    <div
      ref={containerRef}
      className={`fixed inset-0 z-50 flex flex-col ${displayMode ? 'bg-black' : 'bg-gray-950'}`}
      data-testid={displayMode ? 'briefing-display-mode' : 'briefing-preview-modal'}
    >
      {/* Header - hidden in display mode */}
      {!displayMode && (
      <div className="flex items-center justify-between bg-gray-900 border-b border-gray-700 px-6 py-3">
        <div className="flex items-center gap-3">
          <Presentation className="h-5 w-5 text-orange-500" />
          <h2 className="text-white font-semibold">IC Briefing Preview</h2>
          <span className="text-gray-500 text-sm">
            {slides.length > 0 ? `Slide ${currentSlide + 1} of ${slides.length}` : ''}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Button
            onClick={openDisplayMode}
            variant="outline"
            className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10 text-sm px-4 py-2"
            title="Open in a new tab, projection-friendly"
            data-testid="briefing-display-mode-btn"
          >
            <Monitor className="h-4 w-4 mr-2" />
            Display Mode
          </Button>
          <Button
            onClick={handleDownload}
            disabled={downloading || loading}
            className="bg-orange-500 hover:bg-orange-600 text-white text-sm px-4 py-2"
            data-testid="briefing-download-btn"
          >
            {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            Download .pptx
          </Button>
          <Button
            onClick={onClose}
            variant="outline"
            className="border-gray-600 text-gray-300 hover:bg-gray-800 text-sm px-4 py-2"
            data-testid="briefing-back-btn"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to ICS
          </Button>
        </div>
      </div>
      )}

      {/* Slide Content */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className={`${displayMode ? 'h-16 w-16' : 'h-8 w-8'} text-orange-500 animate-spin`} />
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-full text-red-400">{error}</div>
        ) : (
          <div className={displayMode ? 'max-w-[1600px] mx-auto p-10 briefing-display-scale' : 'max-w-5xl mx-auto p-8'}>
            {/* Slide title bar */}
            <div className={`flex items-center gap-3 mb-6 pb-3 border-b border-orange-500/40 ${displayMode ? 'gap-5 mb-10 pb-6' : ''}`}>
              <SlideIcon className={`text-orange-500 ${displayMode ? 'h-10 w-10' : 'h-5 w-5'}`} />
              <h3 className={`font-bold text-white ${displayMode ? 'text-4xl' : 'text-xl'}`}>{slide?.title}</h3>
              {displayMode && (
                <span className="ml-auto text-orange-400 text-2xl font-mono">{currentSlide + 1}<span className="text-gray-500 text-lg"> / {slides.length}</span></span>
              )}
            </div>
            {renderSlideContent()}
          </div>
        )}
      </div>

      {/* Navigation Footer */}
      <div className={`flex items-center justify-between border-t px-6 ${displayMode ? 'bg-black border-gray-800 py-4' : 'bg-gray-900 border-gray-700 py-3'}`}>
        {/* Slide thumbnails */}
        <div className="flex gap-1.5 flex-wrap">
          {slides.map((s, i) => (
            <button
              key={`nav-${i}`}
              onClick={() => setCurrentSlide(i)}
              className={`rounded font-medium transition-colors ${displayMode ? 'w-10 h-10 text-sm' : 'w-8 h-8 text-xs'} ${
                i === currentSlide
                  ? 'bg-orange-500 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
              data-testid={`briefing-slide-${i + 1}`}
            >
              {i + 1}
            </button>
          ))}
        </div>
        <div className="flex gap-2 items-center">
          {displayMode && (
            <>
              <button
                onClick={() => setAutoPlay((v) => !v)}
                className={`flex items-center gap-1 px-3 py-2 rounded border text-sm ${autoPlay ? 'bg-orange-500 border-orange-500 text-white' : 'border-gray-600 text-gray-300 hover:bg-gray-800'}`}
                title={autoPlay ? 'Pause auto-advance (P)' : 'Play auto-advance (P)'}
                data-testid="briefing-display-autoplay"
              >
                {autoPlay ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                {autoPlay ? 'Pause' : 'Auto'}
              </button>
              <select
                value={autoPlaySeconds}
                onChange={(e) => setAutoPlaySeconds(parseInt(e.target.value, 10) || 12)}
                className="bg-gray-800 border border-gray-600 text-gray-300 text-sm rounded px-2 py-2"
                title="Auto-advance interval"
              >
                {[6, 10, 12, 15, 20, 30, 45, 60].map((n) => <option key={n} value={n}>{n}s</option>)}
              </select>
              <button
                onClick={toggleFullscreen}
                className="flex items-center gap-1 px-3 py-2 rounded border border-gray-600 text-gray-300 hover:bg-gray-800 text-sm"
                title="Toggle fullscreen (F)"
                data-testid="briefing-display-fullscreen"
              >
                <Maximize2 className="h-4 w-4" /> Fullscreen
              </button>
            </>
          )}
          <Button
            variant="outline"
            onClick={() => setCurrentSlide(Math.max(0, currentSlide - 1))}
            disabled={currentSlide === 0}
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
            data-testid="briefing-prev-btn"
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Previous
          </Button>
          <Button
            variant="outline"
            onClick={() => setCurrentSlide(Math.min(slides.length - 1, currentSlide + 1))}
            disabled={currentSlide === slides.length - 1}
            className="border-gray-600 text-gray-300 hover:bg-gray-800"
            data-testid="briefing-next-btn"
          >
            Next <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Helper components
const InfoCard = ({ label, value, full }) => (
  <div className={`bg-gray-800/60 rounded-lg p-3 ${full ? '' : ''}`}>
    <div className="text-gray-500 text-xs mb-1">{label}</div>
    <div className="text-white text-sm font-semibold whitespace-pre-line">{value || 'N/A'}</div>
  </div>
);

const StatBadge = ({ label, value }) => (
  <div className="bg-gray-800/60 rounded-lg px-3 py-2 flex items-center gap-2">
    <span className="text-orange-500 text-lg font-bold">{value}</span>
    <span className="text-gray-400 text-sm">{label}</span>
  </div>
);

const ResourceCard = ({ label, value }) => (
  <div className="bg-gray-800/60 border border-orange-500/20 rounded-lg p-3 text-center">
    <div className="text-orange-500 text-2xl font-bold">{value}</div>
    <div className="text-gray-400 text-xs mt-1">{label}</div>
  </div>
);

export default BriefingPreview;
